/**
 * Tailwind CSS implementation of LoaderButton component
 * Renders Button with its appearance classes plus a disabled-dimming layer
 * from loader-button.css.
 */

import * as React from "react";
import Box from "@sproutsocial/seeds-react-box";
import Button from "@sproutsocial/seeds-react-button";
import Loader from "@sproutsocial/seeds-react-loader";
import type { TypeLoaderButtonProps } from "./LoaderButtonTypes";

// Utility for merging class names properly
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

export const LoaderButtonTailwind = ({
  isLoading = false,
  disabled,
  loaderLabel,
  children,
  appearance = "unstyled",
  className,
  ...rest
}: TypeLoaderButtonProps) => {
  const isDisabled = disabled || isLoading;
  const classes =
    cn({ "seeds-loader-btn-disabled": !!isDisabled }, className) || undefined;

  return (
    <Button
      disabled={isDisabled}
      appearance={appearance}
      data-qa-button-isloading={isLoading === true}
      className={classes}
      {...rest}
    >
      <Box position="relative">
        <Box opacity={isLoading ? 0 : 1} aria-hidden={isLoading}>
          {children}
        </Box>
        {isLoading && (
          <Box position="absolute" top={-2} left={0} right={0} bottom={0}>
            <Loader text={loaderLabel} size="small" delay={false} />
          </Box>
        )}
      </Box>
    </Button>
  );
};

export default LoaderButtonTailwind;
